import { useQuery } from "@tanstack/react-query";
import { axiosClient } from "../api/axiosClient";
import { shouldRetryQuery } from "../utils/queryRetry";
import type { CurrentPlanResponse } from "../types/subscription.types";
import { useCachedMedium } from "./useCachedMedium";
import { useCachedTerreiro } from "./useCachedTerreiro";

/**
 * Get whether subscription limits are enabled from environment
 * Exported separately to allow mocking in tests
 */
export const getSubscriptionLimitsEnabled = (): boolean => {
  if (process.env.NODE_ENV === "test") {
    return true;
  }

  try {
    return import.meta.env.VITE_ENABLE_SUBSCRIPTION_LIMITS === "true";
  } catch {
    const maybeImportMeta = (globalThis as {
      import?: { meta?: { env?: Record<string, string | undefined> } };
    }).import?.meta;

    return maybeImportMeta?.env?.VITE_ENABLE_SUBSCRIPTION_LIMITS === "true";
  }
};

/**
 * Constants for feature limit values
 */
export const FEATURE_VALUES = {
  /** -1 indicates unlimited access */
  UNLIMITED: -1,
  /** 0 indicates feature is completely blocked */
  BLOCKED: 0,
} as const;

/**
 * Query key helper for enhanced subscription features
 */
export const enhancedSubscriptionFeaturesQueryKey = (context?: {
  terreiroId?: string;
  mediumId?: string;
}) => {
  const id = context?.terreiroId ?? context?.mediumId ?? "no-context";
  const type = context?.terreiroId
    ? "terreiro"
    : context?.mediumId
      ? "medium"
      : "unknown";
  return ["subscription", type, id, "enhanced-features"] as const;
};

interface UseEnhancedSubscriptionFeaturesProps {
  terreiroId?: string;
  mediumId?: string;
  enabled?: boolean;
}

/**
 * Hook to fetch enhanced subscription features with detailed limit information
 *
 * This hook fetches the new API response format that includes:
 * - context (personal/terreiro)
 * - plan and planName
 * - features with limit, used, remaining, unlimited, and allowed properties
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useEnhancedSubscriptionFeatures();
 *
 * // Check if incomes are allowed
 * const canAddIncome = data?.features.maxIncomesPerMonth?.allowed ?? false;
 * ```
 */
export function useEnhancedSubscriptionFeatures(
  props: UseEnhancedSubscriptionFeaturesProps = {},
) {
  const {
    terreiroId: overrideTerreiroId,
    mediumId: overrideMediumId,
    enabled = true,
  } = props;
  const cachedTerreiro = useCachedTerreiro();
  const cachedMedium = useCachedMedium();

  const callerManaged =
    Object.prototype.hasOwnProperty.call(props, "terreiroId") ||
    Object.prototype.hasOwnProperty.call(props, "mediumId");

  // Determine context: use override if provided, otherwise use cached
  const terreiroId = callerManaged
    ? overrideTerreiroId
    : (overrideTerreiroId ?? cachedTerreiro?.id);
  const mediumId = callerManaged
    ? overrideMediumId
    : (overrideMediumId ?? cachedMedium?.id);

  const isPersonalProfile = callerManaged
    ? !overrideTerreiroId
    : cachedTerreiro === null;

  const hasTerreiro = !isPersonalProfile && !!terreiroId;
  const hasMedium = (isPersonalProfile || !hasTerreiro) && !!mediumId;

  const context = hasTerreiro
    ? { terreiroId }
    : hasMedium
      ? { mediumId }
      : undefined;

  // Enable query only when:
  // 1. Feature flag is on
  // 2. We have a valid context (terreiro OR medium)
  // 3. enabled prop is true
  const shouldEnableQuery =
    enabled && getSubscriptionLimitsEnabled() && !!context;

  return useQuery<CurrentPlanResponse, Error>({
    queryKey: enhancedSubscriptionFeaturesQueryKey(context),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (terreiroId) {
        params.append("terreiroId", terreiroId);
      } else if (mediumId) {
        params.append("mediumId", mediumId);
      }

      const { data } = await axiosClient.get<CurrentPlanResponse>(
        `/subscription/current-plan?${params.toString()}`,
        { withCredentials: true },
      );
      return data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
    refetchOnMount: true, // Refetch when component mounts to handle profile switches
    refetchOnWindowFocus: false, // Don't refetch when window gets focus
    enabled: shouldEnableQuery,
    retry: shouldRetryQuery,
  });
}

/**
 * Helper to check if a specific feature is allowed based on the current plan response
 *
 * @param data - Current plan response from API
 * @param featureKey - API feature key (e.g., 'maxIncomesPerMonth', 'maxOutcomesPerMonth', 'maxBankAccounts')
 * @param currentCount - Optional: current usage count for numeric limits
 * @returns true if feature is allowed, false if blocked or limit reached
 *
 * @example
 * ```tsx
 * // Check without usage count (simple boolean check)
 * const canCreate = isFeatureAllowed(subscriptionData, "maxBankAccounts");
 *
 * // Check with usage count (proper limit validation)
 * const canAddIncome = isFeatureAllowed(subscriptionData, "maxIncomesPerMonth", currentIncomeCount);
 * ```
 *
 * For numeric limits:
 * - UNLIMITED (-1): Always returns true
 * - BLOCKED (0): Always returns false
 * - Positive number: Checks if currentCount < limit (when currentCount provided)
 */
export function isFeatureAllowed(
  data: CurrentPlanResponse | undefined,
  featureKey: string,
  currentCount?: number,
): boolean {
  // If subscription limits are disabled, allow everything
  if (!getSubscriptionLimitsEnabled()) {
    return true;
  }

  // Allow by default if no data (fail-open for better UX)
  if (!data || !data.currentPlan || !data.currentPlan.features) {
    console.warn(
      `[isFeatureAllowed] No subscription data available for feature: ${featureKey}. Failing open (allowing).`,
    );
    return true;
  }

  const features = data.currentPlan.features as unknown as Record<
    string,
    number | boolean
  >;
  const featureValue = features[featureKey];

  // Feature not found - allow by default (fail-open)
  if (featureValue === undefined) {
    console.warn(
      `[isFeatureAllowed] Feature ${featureKey} not found in subscription data. Failing open (allowing).`,
    );
    return true;
  }

  // Boolean features - return the value directly
  if (typeof featureValue === "boolean") {
    console.log(
      `[isFeatureAllowed] Feature ${featureKey} is boolean: ${featureValue}`,
    );
    return featureValue;
  }

  // Numeric features
  if (typeof featureValue === "number") {
    // Unlimited access
    if (featureValue === FEATURE_VALUES.UNLIMITED) {
      console.log(`[isFeatureAllowed] Feature ${featureKey} is UNLIMITED (-1)`);
      return true;
    }

    // Completely blocked
    if (featureValue === FEATURE_VALUES.BLOCKED) {
      console.log(`[isFeatureAllowed] Feature ${featureKey} is BLOCKED (0)`);
      return false;
    }

    // If current count is provided, check against limit
    if (currentCount !== undefined) {
      const allowed = currentCount < featureValue;
      console.log(
        `[isFeatureAllowed] Feature ${featureKey}: currentCount=${currentCount}, limit=${featureValue}, allowed=${allowed}`,
      );
      return allowed;
    }

    // If no current count, just check that limit exists
    // This is a fallback - ideally currentCount should be provided
    return featureValue > 0;
  }

  // Unknown type - allow by default (fail-open)
  return true;
}

/**
 * Helper to get feature limit value from current plan
 *
 * @param data - Current plan response
 * @param featureKey - API feature key
 * @returns Feature limit value (number or boolean) or null if not found
 */
export function getFeatureDetail(
  data: CurrentPlanResponse | undefined,
  featureKey: string,
): number | boolean | null {
  if (!data || !data.currentPlan || !data.currentPlan.features) return null;
  const features = data.currentPlan.features as unknown as Record<
    string,
    number | boolean
  >;
  return features[featureKey] ?? null;
}
